import { apiGet, apiPost } from './http'

/**
 * Typed access to the subscription API (bundles, price computation, contract
 * drafts and Stripe checkout).
 *
 * This module is the generic boundary to the backend contract: building/parsing
 * requests and normalizing API payloads (prices come back as strings in *cents*
 * with snake_case keys). It owns nothing product-specific — the per-product
 * answer → API-parameter mapping lives in `src/subscriptionFlows`.
 */

/** Bundle subcategories understood by the backend decision tree. */
export type BundleSubcategory =
  | 'COMPL_SANTE'
  | 'HOME'
  | 'AVE'
  | 'PME'
  | 'PF'
  | 'SURCOMPL_SANTE'
  | 'AURA'
  | 'SOLIDARITE_EXAM'

/** Raw bundle as returned by the API (prices are strings in cents). */
type RawBundle = {
  id: string
  name: string
  normalized_name?: string
  reference_mysmerra?: number | string
  monthly_price?: string
  annual_price?: string
  /** Stay-based international rate (cents), used by travel packs (PME) that
   *  leave monthly_price/annual_price null. */
  price_international?: string
  currency: string
  label?: string
  category?: string
  subcategory?: string
  coverage_level?: number | string
  recommended?: boolean
  already_subscribed?: boolean
  /** Assia forwarding mode: `automatic` triggers the RIB + NIR collection step. */
  assia_forwarding?: string
  upsell_bundles?: RawBundle[]
}

/**
 * A bundle normalized for the UI: prices converted to euros, camelCase keys.
 * `recommended` flags the formula the decision tree picked from the answers.
 */
export type Bundle = {
  id: string
  name: string
  /** Slugified bundle name (the API's `normalized_name`), used as the i18n key
   *  to translate the bundle label — see the `bundles` namespace in the locales. */
  key: string | null
  /** Stable business reference, used to match a bundle to a journey plan slug. */
  referenceMysmerra: number | null
  label: string | null
  category: string | null
  subcategory: string | null
  coverageLevel: number | null
  /** Monthly price in euros, or null when the API omits it. */
  monthlyPrice: number | null
  /** Annual price in euros, or null when the API omits it. */
  annualPrice: number | null
  /** Stay-based international rate in euros (travel packs), or null. */
  priceInternational: number | null
  currency: string
  recommended: boolean
  alreadySubscribed: boolean
  /** Assia forwarding mode; `'automatic'` gates the RIB + NIR collection. */
  assiaForwarding: string | null
  upsellBundles: Bundle[]
}

/** Convert a price given as a string in cents (API format) to euros.
 *  Returns null when absent so the UI can tell "no price" apart from 0 €. */
function centsToEuros(value: string | undefined): number | null {
  if (value == null || value === '') return null
  const cents = Number(value)
  return Number.isFinite(cents) ? cents / 100 : null
}

function toNullableNumber(value: number | string | undefined): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Map a raw API bundle to the normalized UI shape (recursively for upsells). */
function normalizeBundle(raw: RawBundle): Bundle {
  return {
    id: raw.id,
    name: raw.name,
    key: raw.normalized_name ?? null,
    referenceMysmerra: toNullableNumber(raw.reference_mysmerra),
    label: raw.label ?? null,
    category: raw.category ?? null,
    subcategory: raw.subcategory ?? null,
    coverageLevel: toNullableNumber(raw.coverage_level),
    monthlyPrice: centsToEuros(raw.monthly_price),
    annualPrice: centsToEuros(raw.annual_price),
    priceInternational: centsToEuros(raw.price_international),
    currency: raw.currency,
    recommended: raw.recommended ?? false,
    alreadySubscribed: raw.already_subscribed ?? false,
    assiaForwarding: raw.assia_forwarding ?? null,
    upsellBundles: (raw.upsell_bundles ?? []).map(normalizeBundle),
  }
}

/** Query parameters accepted by `GET /subscription/available_bundles`. */
export type AvailableBundlesParams = {
  subcategory: BundleSubcategory
  doctors?: 'often' | 'rarely' | 'no'
  optical?: 'often' | 'rarely' | 'no'
  dental?: 'yes_prosthesis' | 'yes' | 'no'
  internetProtection?: boolean
  destination?: 'europe' | 'monde'
  travelDuration?: 'less_than_3' | 'between_3_and_8'
}

/** Fetch the available bundles for a subscription flow, with one flagged as
 *  `recommended` by the backend decision tree. Throws `ApiError` on failure. */
export async function fetchAvailableBundles(
  params: AvailableBundlesParams,
  signal?: AbortSignal,
): Promise<Bundle[]> {
  const data = await apiGet<{ bundles: RawBundle[] }>('/subscription/available_bundles', {
    query: {
      subcategory: params.subcategory,
      doctors: params.doctors,
      optical: params.optical,
      dental: params.dental,
      internet_protection: params.internetProtection,
      destination: params.destination,
      travel_duration: params.travelDuration,
    },
    signal,
  })
  return data.bundles.map(normalizeBundle)
}

/** Query parameters accepted by `GET /subscription/compute_price`. */
export type ComputePriceParams = {
  bundleId: string
  /** Inclusive start date, formatted YYYY-MM-DD. */
  effectiveFrom: string
  /** Inclusive end date, formatted YYYY-MM-DD. */
  effectiveUntil: string
}

/** Compute the price of a bundle over a date range, returned in euros (the API
 *  returns cents). Throws `ApiError` (404 unknown bundle, 422 invalid dates). */
export async function computePrice(params: ComputePriceParams, signal?: AbortSignal): Promise<number> {
  const data = await apiGet<{ price: number }>('/subscription/compute_price', {
    query: {
      bundle_id: params.bundleId,
      effective_from: params.effectiveFrom,
      effective_until: params.effectiveUntil,
    },
    signal,
  })
  return data.price / 100
}

/* ------------------------------------------------------------------
 * Contract draft + Stripe checkout (subscription finalization).
 * The journey collects the subscriber's details and the chosen bundle(s); we
 * persist them as a `contract_draft`, then open a Stripe Checkout session for
 * that draft and let the caller redirect to the returned URL. The shape of the
 * draft `data` (users/contracts) mirrors the backend validators, so the mapping
 * stays in this single boundary module.
 * ------------------------------------------------------------------*/

/** A postal address as expected in a contract draft user. */
export type ContractDraftAddress = {
  address_line1: string
  address_line2?: string
  zipcode: string
  city: string
  country: string
}

/** Beneficiary banking details (SEPA), required for health when a RIB is given. */
export type ContractDraftBanking = {
  owner_fullname: string
  iban: string
  bic: string
}

/** A guest user inside a contract draft (no account yet): full personal info. */
export type ContractDraftGuestUser = {
  user_role: 'beneficiary' | 'subscriber'
  civility: 'mr' | 'mrs'
  firstname: string
  lastname: string
  email: string
  birthdate: string
  address: ContractDraftAddress
  phone_number?: string
  social_security_number?: string
  banking_account_details?: ContractDraftBanking
  /** Commercial prospecting consent. Guest-only: the backend rejects it on a
   *  connected user, whose consent goes through `PATCH /users` instead. */
  prospecting_opt_in?: boolean
}

/** A connected user inside a contract draft: identified by account UUID. The
 *  backend accepts only `user_id`, `user_role` and an optional
 *  `banking_account_details` for a connected user (any other field is rejected):
 *  the rest of the personal info is already on file server-side. */
export type ContractDraftConnectedUser = {
  user_id: string
  user_role: 'beneficiary' | 'subscriber'
  /** Beneficiary RIB, sent only when provided in the journey (health flows). */
  banking_account_details?: ContractDraftBanking
}

/** A user line in a contract draft — guest (full info) or connected (by UUID). */
export type ContractDraftUser = ContractDraftGuestUser | ContractDraftConnectedUser

/** A single contract line inside a contract draft. */
export type ContractDraftContract = {
  bundle_id: string
  effective_from: string
  effective_until: string
  tacit_renewal: boolean
  subscriber_profile: 'student' | 'parent'
  origin: 'website'
  /** Product-specific metadata (e.g. study-abroad destination country code). */
  metadata?: Record<string, string>
}

/** The full `data` payload of a contract draft. */
export type ContractDraftData = {
  users: ContractDraftUser[]
  contracts: ContractDraftContract[]
}

/** A contract draft as returned by the API (only the fields the front needs). */
export type ContractDraft = {
  id: string
  status: string
}

/** Stripe checkout modes accepted by the payments endpoint. */
export type CheckoutMode = 'subscription' | 'one_time_payment'

/** A Stripe Checkout session: the URL the front redirects the browser to. */
export type CheckoutSession = {
  url: string
  checkoutSessionId: string
}

/** Normalized subscriber details collected by the info-form step. */
export type SubscriberInfo = {
  civility: 'mr' | 'mrs'
  firstName: string
  lastName: string
  /** ISO date, YYYY-MM-DD. */
  birthdate: string
  email: string
  address: ContractDraftAddress
  /** International phone number, or undefined when not provided. */
  phoneNumber?: string
  /** 15-char French SSN, or undefined when the user declared having none. */
  socialSecurityNumber?: string
  /** SEPA details, or undefined when the user chose to provide a RIB later. */
  banking?: ContractDraftBanking
  /** Commercial prospecting consent ticked (or not) on the payment recap. */
  prospectingOptIn: boolean
}

/** One contract to finalize: a bundle over the chosen period. */
export type ContractInput = {
  bundleId: string
  effectiveFrom: string
  effectiveUntil: string
}

/**
 * Build the contract draft `data` from the subscriber's details and the chosen
 * bundle(s). The beneficiary is the only user; each bundle (the base formula
 * and, when subscribed, a top-up) becomes its own contract.
 *
 * Two subscriber shapes, decided by `userId`:
 * - connected (userId set): the backend identifies the user by account UUID, so
 *   we send `{ user_id, user_role }` plus the RIB (`banking_account_details`)
 *   when it was collected — the only personal fields a connected draft accepts.
 *   Its prospecting consent is carried by `PATCH /users`, never by the draft.
 * - guest (userId falsy): the full personal info collected in the journey, plus
 *   the prospecting consent that becomes the created account's.
 */
export function buildContractDraft(params: {
  info: SubscriberInfo
  contracts: ContractInput[]
  subscriberProfile: ContractDraftContract['subscriber_profile']
  tacitRenewal: boolean
  /** Product-specific contract metadata, attached to every contract line. */
  metadata?: Record<string, string> | null
  /** Account UUID of the logged-in subscriber. When set, build a connected
   *  user (user_id + user_role only); otherwise a guest user with full info. */
  userId?: string | null
}): ContractDraftData {
  const { info, contracts, subscriberProfile, tacitRenewal, metadata, userId } = params
  const user: ContractDraftUser = userId
    ? {
        user_id: userId,
        user_role: 'beneficiary',
        ...(info.banking ? { banking_account_details: info.banking } : {}),
      }
    : {
        user_role: 'beneficiary',
        civility: info.civility,
        firstname: info.firstName,
        lastname: info.lastName,
        email: info.email,
        birthdate: info.birthdate,
        address: info.address,
        ...(info.phoneNumber ? { phone_number: info.phoneNumber } : {}),
        ...(info.socialSecurityNumber ? { social_security_number: info.socialSecurityNumber } : {}),
        ...(info.banking ? { banking_account_details: info.banking } : {}),
        prospecting_opt_in: info.prospectingOptIn,
      }
  return {
    users: [user],
    contracts: contracts.map((c) => ({
      bundle_id: c.bundleId,
      effective_from: c.effectiveFrom,
      effective_until: c.effectiveUntil,
      tacit_renewal: tacitRenewal,
      subscriber_profile: subscriberProfile,
      origin: 'website',
      ...(metadata ? { metadata } : {}),
    })),
  }
}

/**
 * True when a contract-draft validation body reports the subscriber's email as
 * already taken (a registered account exists). The backend nests these
 * per-attribute errors inside the draft `data`, so we deep-search the body for
 * the matching `{ attribute: 'email', code: 'taken' }` entry rather than rely on
 * a fixed shape.
 */
export function isEmailTakenError(body: unknown): boolean {
  let found = false
  const visit = (node: unknown): void => {
    if (found || node === null || typeof node !== 'object') return
    if (Array.isArray(node)) {
      node.forEach(visit)
      return
    }
    const record = node as Record<string, unknown>
    if (record.attribute === 'email' && record.code === 'taken') {
      found = true
      return
    }
    Object.values(record).forEach(visit)
  }
  visit(body)
  return found
}

/** Create a contract draft. Throws `ApiError` (422 carries the validation body). */
export async function createContractDraft(data: ContractDraftData, signal?: AbortSignal): Promise<ContractDraft> {
  const res = await apiPost<{ contract_draft: ContractDraft }>(
    '/subscription/contract_drafts',
    { contract_draft: { data } },
    { signal },
  )
  return res.contract_draft
}

/** Open a Stripe Checkout session for a contract draft. Throws `ApiError`. */
export async function createCheckoutSession(
  contractDraftId: string,
  mode: CheckoutMode,
  signal?: AbortSignal,
): Promise<CheckoutSession> {
  const res = await apiPost<{ url: string; checkout_session_id: string }>(
    '/subscription/payments/create_checkout_session',
    { contract_draft_id: contractDraftId, mode },
    { signal },
  )
  return { url: res.url, checkoutSessionId: res.checkout_session_id }
}

/** A Stripe Payment Link: the shareable URL a third party opens to pay for the
 *  draft. Same `mode` semantics as checkout (upfront → one_time_payment). */
export type PaymentLink = {
  url: string
  paymentLinkId: string
  contractDraftId: string
}

/**
 * Create (or, since the endpoint is idempotent per draft, retrieve) a shareable
 * Stripe Payment Link for a contract draft, so a third party can pay on the
 * subscriber's behalf. Same anonymous flow as checkout — no bearer token.
 * Throws `ApiError`: 404 (draft not found), 400 (subscription unavailable for
 * PME), 422 (draft not eligible / bad or missing mode / conversion already
 * failed / missing user / unable to create link).
 */
export async function createPaymentLink(
  contractDraftId: string,
  mode: CheckoutMode,
  signal?: AbortSignal,
): Promise<PaymentLink> {
  const res = await apiPost<{
    url: string
    payment_link_id: string
    contract_draft_id: string
    platform: string
  }>(
    '/subscription/payments/create_payment_link',
    { contract_draft_id: contractDraftId, mode },
    { signal },
  )
  return { url: res.url, paymentLinkId: res.payment_link_id, contractDraftId: res.contract_draft_id }
}
